import { useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import { userActions } from "../../../actions/user.actions";

const COLUMNS = 6;

const differenceText = (version) => {
  if (version.isCurrent) return "—";
  if (!version.changedThresholds) return "Same as live";
  return `${version.changedThresholds} threshold${version.changedThresholds > 1 ? "s" : ""} different`;
};

// Versions newest first. Revert puts an older version live again as a new version, with a reason
export default function VersionHistory({ policyId, versions, loading, error, onReverted }) {
  const dispatch = useDispatch();
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [isReverting, setIsReverting] = useState(false);

  const openRevert = (version) => {
    setTarget(version);
    setReason("");
    setErrorMessage(null);
  };

  const handleRevert = () => {
    setIsReverting(true);
    setErrorMessage(null);
    dispatch(userActions.rollbackRinuxRule(policyId, target.version, reason.trim()))
      .then((data) => {
        onReverted(`Reverted to v${target.version}. It is live now as v${data?.version}`);
        setTarget(null);
      })
      .catch((err) => {
        const data = err?.response?.data;
        setErrorMessage([data?.message || "Could not revert.", ...(data?.validation?.blocking || [])].join(" · "));
      })
      .finally(() => setIsReverting(false));
  };

  return (
    <Box>
      {error && <Box sx={{ color: "error.main", marginBottom: "12px" }}>{error}</Box>}

      <Paper variant="outlined" sx={{ borderRadius: "8px" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#F8F8F8" }}>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Published By</TableCell>
                <TableCell>Published At</TableCell>
                <TableCell>Compared to Live</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !versions.length &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: COLUMNS }).map((_, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {versions.map((version) => (
                <TableRow key={version.version} hover>
                  <TableCell sx={{ fontWeight: 500 }}>v{version.version}</TableCell>
                  <TableCell>
                    {version.publishedBy === "outside editor" ? (
                      <Box component="span" sx={{ color: "text.secondary" }}>Changed outside the editor</Box>
                    ) : (
                      version.publishedBy
                    )}
                  </TableCell>
                  <TableCell>{moment(version.publishedAt).format("DD MMM YYYY, hh:mm A")}</TableCell>
                  <TableCell>{differenceText(version)}</TableCell>
                  <TableCell>
                    {version.isCurrent ? <Chip size="small" variant="fibePill" color="success" label="Live" /> : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {!version.isCurrent && (
                      <Button size="small" startIcon={<UndoIcon fontSize="small" />} onClick={() => openRevert(version)}>
                        Revert
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !versions.length && (
                <TableRow>
                  <TableCell colSpan={COLUMNS} align="center" sx={{ color: "text.secondary", padding: "32px" }}>
                    No versions yet. The first publish saves today's rules as v1 and your change as v2.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(target)} onClose={isReverting ? undefined : () => setTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Revert to v{target?.version}</DialogTitle>
        <DialogContent>
          {errorMessage && <Alert severity="error" sx={{ marginBottom: "16px" }}>{errorMessage}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "16px" }}>
            The rules of v{target?.version} go live again as a new version. Applicants who were already decided keep their
            result.
          </Typography>
          <TextField
            label="Reason"
            placeholder="Why are you reverting?"
            required
            fullWidth
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ marginTop: "8px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)} disabled={isReverting}>Cancel</Button>
          <Button variant="contained" onClick={handleRevert} disabled={!reason.trim() || isReverting} sx={{ color: "#fff" }}>
            {isReverting ? "Reverting..." : "Revert"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
